/**
 * Vista de Inversiones y Cartera de FinVoz (FR-INVEST-1..8, FR-MKT-1..8).
 *
 * Principios no negociables:
 *  - Catálogo buscable de acciones, ETF, bonos y fondos puramente informativo.
 *  - Separación estricta entre:
 *    a) "Registrar instrumento": sólo inventario/catálogo, sin asiento contable ni cambio de saldo.
 *    b) "Comprar": cantidad, precio y cuenta de origen obligatorios; validación previa de saldo.
 *       Asiento: Debe inversión / Haber efectivo-banco-broker.
 *    c) "Venta": no permite vender más de la cantidad disponible.
 *       Asiento: Debe efectivo-banco-broker / Haber inversión.
 *  - Archivar instrumento: no borra historial contable, sólo archiva la cuenta (FR-ACCT-5).
 *  - La valoración a mercado y plusvalía latente son informativas y NUNCA escriben en el Libro Diario (INV-4, INV-7).
 */

import type { AppState } from "../types.js";
import { formatMinorUnits } from "../format.js";
import { escapeHtml, escapeAttr } from "../sanitize.js";
import { costBasis, instrumentQuantity, accountBalance } from "../../core/ledger.js";
import type { PriceQuote } from "../../market/quote-provider.js";
import { INVESTMENT_CATALOG, type CatalogInstrument } from "../../market/catalog.js";
import { MARKET_SEGMENTS, type MarketSegment, type MarketInstrumentRecord } from "../../market/global-catalog.js";

export function formatVolume(vol: number | undefined): string {
  if (vol === undefined || isNaN(vol)) return "—";
  if (vol >= 1_000_000_000) return `${(vol / 1_000_000_000).toFixed(2)}B`;
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(2)}M`;
  if (vol >= 1_000) return `${(vol / 1_000).toFixed(1)}K`;
  return vol.toLocaleString();
}

export function formatChangePercent(chg: number | undefined): string {
  if (chg === undefined || isNaN(chg)) return "";
  const sign = chg > 0 ? "+" : "";
  const color = chg >= 0 ? "var(--success-text, #10b981)" : "var(--danger-text, #ef4444)";
  return `<span style="font-size: 0.75rem; font-weight: 600; color: ${color}; margin-left: 4px;">${sign}${chg.toFixed(2)}%</span>`;
}

export interface PositionViewItem {
  readonly accountId: string;
  readonly accountName: string;
  readonly instrumentId: string;
  readonly archived: boolean;
  readonly quantityMinor: bigint;
  readonly costBasisMinor: bigint;
  readonly quote: PriceQuote | undefined;
  readonly marketValueMinor: bigint;
  readonly unrealizedPnLMinor: bigint;
  readonly currency: string;
}

export function renderInvestments(
  state: AppState,
  quotes: readonly PriceQuote[],
  marketRecords?: readonly MarketInstrumentRecord[],
): string {
  const currency = state.currency || "PEN";
  const scale = 2;
  const rawEntries = state.entries.map((e) => e.entry);

  // Cuentas de inversión legítimas (activos con instrumentId real, ignorando residuales ficticios)
  const allInvestmentAccounts = state.accounts.filter(
    (a) => a.type === "asset" && Boolean(a.instrumentId) && a.instrumentId !== "GLOBAL-EQ",
  );
  const activeInvestmentAccounts = allInvestmentAccounts.filter((a) => !a.archived);

  // Cuentas de liquidez / fondos disponibles (efectivo, banco, broker sin instrumento)
  const liquidityAccounts = state.accounts.filter(
    (a) => a.type === "asset" && !a.archived && !a.instrumentId,
  );

  const positions: PositionViewItem[] = [];

  for (const acc of activeInvestmentAccounts) {
    const instrumentId = acc.instrumentId!;
    const qty = instrumentQuantity(acc.accountId, rawEntries);
    const posCurrency = acc.currency || currency;
    const cb = costBasis(acc.accountId, rawEntries, posCurrency, scale).amountMinor;

    // Buscar última cotización conocida en memoria o quoteCache
    const quote = quotes
      .filter((q) => q.instrumentId === instrumentId)
      .sort((a, b) => (b.asOf > a.asOf ? 1 : -1))[0];

    let marketValueMinor = cb;
    let unrealizedPnLMinor = 0n;

    if (quote && qty > 0n) {
      const rawVal = (qty * quote.priceMinor) / (10n ** BigInt(quote.scale));
      marketValueMinor = rawVal;
      unrealizedPnLMinor = marketValueMinor - cb;
    }

    positions.push({
      accountId: acc.accountId,
      accountName: acc.name,
      instrumentId,
      archived: acc.archived,
      quantityMinor: qty,
      costBasisMinor: cb,
      quote,
      marketValueMinor,
      unrealizedPnLMinor,
      currency: posCurrency,
    });
  }

  // Agrupar totales estrictamente por divisa (NFR-MONEY-1: prohibido sumar minor units de monedas distintas)
  interface CurrencyPortfolioTotal {
    currency: string;
    costBasisMinor: bigint;
    marketValueMinor: bigint;
    unrealizedPnLMinor: bigint;
    count: number;
  }

  const totalsByCurrency = new Map<string, CurrencyPortfolioTotal>();
  for (const pos of positions) {
    const cur = pos.currency || currency;
    const existing = totalsByCurrency.get(cur) || {
      currency: cur,
      costBasisMinor: 0n,
      marketValueMinor: 0n,
      unrealizedPnLMinor: 0n,
      count: 0,
    };
    existing.costBasisMinor += pos.costBasisMinor;
    existing.marketValueMinor += pos.marketValueMinor;
    existing.unrealizedPnLMinor += pos.unrealizedPnLMinor;
    existing.count += 1;
    totalsByCurrency.set(cur, existing);
  }

  // Totales correspondientes a la moneda activa configurada en la aplicación
  const activeCurrencyTotal = totalsByCurrency.get(currency) || {
    currency,
    costBasisMinor: 0n,
    marketValueMinor: 0n,
    unrealizedPnLMinor: 0n,
    count: 0,
  };

  // Otras monedas presentes en la cartera de posiciones (mostradas de forma aislada sin mezclar)
  const otherCurrencyTotals = Array.from(totalsByCurrency.values()).filter((t) => t.currency !== currency);

  return `
    <div class="investments-view" data-testid="investments-view">
      <!-- Advertencia regulatoria y de mercado (FR-MKT-5) -->
      <div style="background-color: var(--bg-tertiary); border-left: 4px solid var(--accent); padding: 12px 16px; border-radius: 6px; margin-bottom: 20px; font-size: 0.85rem;">
        <strong>Aviso informativo de mercado (FR-MKT-5):</strong> Las cotizaciones y el catálogo son puramente informativos y diferidos. La valoración latente no genera asientos en el Libro Diario (INV-7).
      </div>

      <!-- Resumen de Cartera (Aislado por divisa - NFR-MONEY-1) -->
      <div class="grid-3" style="margin-bottom: 20px;">
        <div class="card">
          <span class="metric-label">Valor de Mercado Total (${escapeHtml(currency)})</span>
          <div class="metric-value" data-testid="total-market-value">
            ${formatMinorUnits(activeCurrencyTotal.marketValueMinor, currency, scale)}
          </div>
          <div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 4px;">
            Coste base: ${formatMinorUnits(activeCurrencyTotal.costBasisMinor, currency, scale)}
          </div>
          ${otherCurrencyTotals.length > 0 ? `
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid var(--border-color); font-size: 0.75rem; color: var(--text-secondary);">
              <span style="font-weight: 600;">Otras divisas en cartera:</span>
              ${otherCurrencyTotals.map((t) => `
                <div style="margin-top: 2px;">
                  <strong style="color: var(--text-primary);">${escapeHtml(t.currency)}:</strong> ${formatMinorUnits(t.marketValueMinor, t.currency, scale)} (Coste: ${formatMinorUnits(t.costBasisMinor, t.currency, scale)})
                </div>
              `).join("")}
            </div>
          ` : ""}
        </div>

        <div class="card">
          <span class="metric-label">Plusvalía / Minusvalía Latente (${escapeHtml(currency)})</span>
          <div class="metric-value ${activeCurrencyTotal.unrealizedPnLMinor >= 0n ? 'metric-positive' : 'metric-negative'}" data-testid="total-pnl">
            ${activeCurrencyTotal.unrealizedPnLMinor >= 0n ? '+' : ''}${formatMinorUnits(activeCurrencyTotal.unrealizedPnLMinor, currency, scale)}
          </div>
          <div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 4px;">
            Rendimiento no realizado (informativo)
          </div>
          ${otherCurrencyTotals.length > 0 ? `
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid var(--border-color); font-size: 0.75rem; color: var(--text-secondary);">
              <span style="font-weight: 600;">P&L otras divisas:</span>
              ${otherCurrencyTotals.map((t) => `
                <div style="margin-top: 2px;" class="${t.unrealizedPnLMinor >= 0n ? 'metric-positive' : 'metric-negative'}">
                  <strong>${escapeHtml(t.currency)}:</strong> ${t.unrealizedPnLMinor >= 0n ? '+' : ''}${formatMinorUnits(t.unrealizedPnLMinor, t.currency, scale)}
                </div>
              `).join("")}
            </div>
          ` : ""}
        </div>

        <div class="card" style="display: flex; flex-direction: column; justify-content: center; gap: 8px;">
          <div style="display: flex; gap: 8px;">
            <button class="btn-primary" style="flex: 1;" data-testid="open-buy-modal-btn" data-action="open-buy-modal">
              🛒 Comprar
            </button>
            <button class="btn-secondary" style="flex: 1;" data-testid="open-sell-modal-btn" data-action="open-sell-modal">
              💰 Vender
            </button>
          </div>
          <div style="display: flex; gap: 8px;">
            <button class="btn-secondary" style="flex: 1; font-size: 0.8rem;" data-testid="refresh-quotes-btn" data-action="refresh-quotes">
              🔄 Cotizaciones
            </button>
            <button class="btn-secondary" style="flex: 1; font-size: 0.8rem;" data-testid="manual-quote-btn" data-action="open-manual-quote-modal">
              ✏️ Precio Manual
            </button>
          </div>
        </div>
      </div>

      <!-- Tabla de Posiciones en Cartera -->
      <div class="card" style="margin-bottom: 24px;">
        <div class="card-header" style="flex-wrap: wrap; gap: 8px;">
          <div>
            <h2 class="card-title">Posiciones en Cartera (${positions.length})</h2>
            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 2px;">
              Derivadas exclusivamente de compras y ventas confirmadas en el Libro Diario (INV-4).
            </div>
          </div>
          <button class="btn-secondary" data-testid="open-register-instrument-modal-btn" data-action="open-register-instrument-modal">
            ➕ Registrar Instrumento
          </button>
        </div>

        ${positions.length === 0
          ? `<div style="text-align: center; color: var(--text-muted); padding: 32px 0;">
               No tienes posiciones registradas. Puedes explorar el catálogo inferior para registrar instrumentos o ejecutar una compra.
             </div>`
          : `
            <div class="table-container">
              <table data-testid="positions-table">
                <thead>
                  <tr>
                    <th>Instrumento</th>
                    <th>Cantidad</th>
                    <th>Coste Base</th>
                    <th>Cotización (Fecha / Origen)</th>
                    <th>Valor Mercado</th>
                    <th>P&L Latente</th>
                    <th style="text-align: right;">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  ${positions.map((pos) => {
                    const pnlPos = pos.unrealizedPnLMinor >= 0n;
                    const hasQuote = Boolean(pos.quote);

                    return `
                      <tr data-testid="position-row-${escapeAttr(pos.instrumentId)}">
                        <td>
                          <div style="font-weight: 600;">${escapeHtml(pos.accountName)}</div>
                          <div style="font-size: 0.75rem; color: var(--accent); font-family: var(--font-mono);">
                            ${escapeHtml(pos.instrumentId)} • ${escapeHtml(pos.currency)}
                          </div>
                        </td>
                        <td style="font-family: var(--font-mono); font-weight: 600;" data-testid="pos-qty-${escapeAttr(pos.instrumentId)}">
                          ${escapeHtml(pos.quantityMinor.toString())}
                        </td>
                        <td style="font-family: var(--font-mono);">
                          ${formatMinorUnits(pos.costBasisMinor, pos.currency, scale)}
                        </td>
                        <td>
                          ${hasQuote
                            ? `
                              <div style="font-family: var(--font-mono); font-weight: 600;">
                                ${formatMinorUnits(pos.quote!.priceMinor, pos.quote!.currency, pos.quote!.scale)}
                              </div>
                              <div style="font-size: 0.75rem; color: var(--text-muted);">
                                ${escapeHtml(pos.quote!.asOf)} (${escapeHtml(pos.quote!.manual ? 'Manual' : pos.quote!.source)})
                              </div>
                            `
                            : `<span class="badge" style="background-color: var(--bg-tertiary); color: var(--warning);">A coste base</span>`
                          }
                        </td>
                        <td style="font-family: var(--font-mono); font-weight: 600;">
                          ${formatMinorUnits(pos.marketValueMinor, pos.currency, scale)}
                        </td>
                        <td style="font-family: var(--font-mono); font-weight: 600; color: ${pnlPos ? 'var(--success)' : 'var(--danger)'};">
                          ${hasQuote ? `${pnlPos ? '+' : ''}${formatMinorUnits(pos.unrealizedPnLMinor, pos.currency, scale)}` : formatMinorUnits(0n, pos.currency, scale)}
                        </td>
                        <td style="text-align: right; white-space: nowrap;">
                          <div style="display: inline-flex; gap: 4px;">
                            <button
                              class="btn-secondary"
                              style="font-size: 0.75rem; padding: 4px 8px;"
                              data-action="open-buy-modal"
                              data-instrument-id="${escapeAttr(pos.instrumentId)}"
                              data-account-id="${escapeAttr(pos.accountId)}"
                              title="Comprar más unidades"
                            >
                              Comprar
                            </button>
                            ${pos.quantityMinor > 0n
                              ? `<button
                                  class="btn-secondary"
                                  style="font-size: 0.75rem; padding: 4px 8px;"
                                  data-action="open-sell-modal"
                                  data-instrument-id="${escapeAttr(pos.instrumentId)}"
                                  data-account-id="${escapeAttr(pos.accountId)}"
                                  title="Vender unidades"
                                >
                                  Vender
                                </button>`
                              : ""
                            }
                            <button
                              class="btn-secondary"
                              style="font-size: 0.75rem; padding: 4px 8px; color: var(--danger);"
                              data-action="archive-instrument-account"
                              data-account-id="${escapeAttr(pos.accountId)}"
                              title="Archivar instrumento (conserva histórico de asientos)"
                            >
                              Archivar
                            </button>
                          </div>
                        </td>
                      </tr>
                    `;
                  }).join("")}
                </tbody>
              </table>
            </div>
          `}
      </div>

      <!-- Terminal de Mercado Global (+60,000 Instrumentos: Acciones, ETFs, Bonos, Fondos) -->
      <div class="card">
        <div class="card-header" style="flex-wrap: wrap; gap: 8px;">
          <div>
            <h2 class="card-title">Terminal de Mercado Global (+60,000 Activos)</h2>
            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 2px;">
              Catálogo de referencia para traders e inversores. Cotizaciones en tiempo real y volumen de negociación vía Yahoo Finance.
            </div>
          </div>
          <div style="display: flex; gap: 8px; align-items: center;">
            <input
              type="text"
              id="catalog-search-input"
              data-testid="catalog-search-input"
              placeholder="Buscar símbolo (MU, AMZN, BAP, VOO), empresa o ISIN..."
              value="${escapeAttr(state.marketSearchQuery || "")}"
              style="font-size: 0.85rem; padding: 6px 12px; width: 340px; max-width: 100%;"
            />
          </div>
        </div>

        <!-- Selector de Segmentos de Mercado -->
        <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; align-items: center;">
          <span style="font-size: 0.8rem; font-weight: 600; color: var(--text-secondary);">Mercados:</span>
          ${MARKET_SEGMENTS.map((seg) => {
            const activeSegment = state.selectedMarketSegment || "us";
            const isSelected = seg.id === activeSegment;
            return `
              <button
                type="button"
                class="${isSelected ? "btn-primary" : "btn-secondary"}"
                style="font-size: 0.8rem; padding: 4px 10px; display: inline-flex; align-items: center; gap: 6px; cursor: pointer;"
                data-action="select-market-segment"
                data-segment="${seg.id}"
                title="${escapeAttr(seg.description)}"
              >
                <span>${seg.icon}</span>
                <span>${seg.label}</span>
                <span style="opacity: 0.75; font-size: 0.7rem; font-family: var(--font-mono);">(${seg.estimatedCount > 1000 ? (seg.estimatedCount / 1000).toFixed(1) + "k" : seg.estimatedCount})</span>
              </button>
            `;
          }).join("")}
        </div>

        <div class="table-container">
          <table data-testid="catalog-table">
            <thead>
              <tr>
                <th>Símbolo / ISIN</th>
                <th>Nombre</th>
                <th>Bolsa</th>
                <th>Tipo</th>
                <th>Moneda</th>
                <th>Precio Ref. / En Vivo</th>
                <th>Volumen Diario</th>
                <th>Fuente / Fecha Consulta</th>
                <th style="text-align: right;">Acciones</th>
              </tr>
            </thead>
            <tbody id="catalog-tbody">
              ${(() => {
                const activeSegment = state.selectedMarketSegment || "us";
                const itemsToRender = (marketRecords && marketRecords.length > 0)
                  ? marketRecords.slice(0, 60).map((r) => ({
                      symbol: r.s,
                      name: r.n,
                      exchange: r.e,
                      category: r.t,
                      isin: r.i,
                      currency: activeSegment === "latam" && (r.s.endsWith("C1") || r.e === "BVL") ? "PEN" : "USD",
                      referencePriceMinor: undefined as bigint | undefined,
                      source: "Yahoo Finance",
                      asOf: new Date().toISOString().slice(0, 10),
                      queryTimestamp: new Date().toISOString(),
                      scale: 2,
                    }))
                  : INVESTMENT_CATALOG.map((c) => ({
                      symbol: c.symbol,
                      name: c.name,
                      exchange: c.currency === "PEN" ? "BVL" : "US",
                      category: c.category,
                      isin: c.isin,
                      currency: c.currency,
                      referencePriceMinor: c.referencePriceMinor,
                      source: c.source,
                      asOf: c.asOf,
                      queryTimestamp: c.queryTimestamp,
                      scale: c.scale,
                    }));

                return itemsToRender.map((item) => {
                  const isRegistered = allInvestmentAccounts.some(
                    (a) => a.instrumentId === item.symbol && !a.archived,
                  );
                  const quote = quotes.find((q) => q.instrumentId === item.symbol);
                  const itemCurrency = quote?.currency || item.currency;
                  const itemPrice = quote?.priceMinor ?? item.referencePriceMinor;
                  const scale = quote?.scale ?? item.scale;

                  return `
                    <tr
                      data-catalog-symbol="${escapeAttr(item.symbol)}"
                      data-catalog-name="${escapeAttr(item.name.toLowerCase())}"
                      data-catalog-type="${escapeAttr(item.category.toLowerCase())}"
                    >
                      <td>
                        <span style="font-weight: 700; font-family: var(--font-mono);">${escapeHtml(item.symbol)}</span>
                        ${item.isin ? `<div style="font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">${escapeHtml(item.isin)}</div>` : ""}
                      </td>
                      <td style="font-weight: 500;">${escapeHtml(item.name)}</td>
                      <td>
                        <span class="badge" style="background-color: var(--bg-tertiary); color: var(--text-primary); font-size: 0.75rem;">
                          ${escapeHtml(item.exchange)}
                        </span>
                      </td>
                      <td>
                        <span class="badge" style="font-size: 0.75rem;">${escapeHtml(item.category)}</span>
                      </td>
                      <td><span class="badge" style="font-family: var(--font-mono);">${escapeHtml(itemCurrency)}</span></td>
                      <td style="font-family: var(--font-mono); font-weight: 600;">
                        ${itemPrice !== undefined
                          ? `<div>${formatMinorUnits(itemPrice, itemCurrency, scale)} ${formatChangePercent(quote?.changePercent)}</div>`
                          : `<button
                              type="button"
                              class="btn-secondary"
                              style="font-size: 0.75rem; padding: 2px 6px;"
                              data-action="live-quote-symbol"
                              data-symbol="${escapeAttr(item.symbol)}"
                              data-exchange="${escapeAttr(item.exchange)}"
                              title="Consultar precio en tiempo real vía Yahoo Finance"
                            >
                              ⚡ Cotizar
                            </button>`
                        }
                      </td>
                      <td style="font-size: 0.8rem; font-family: var(--font-mono); color: var(--text-secondary);">
                        ${formatVolume(quote?.volume)}
                      </td>
                      <td style="font-size: 0.8rem; color: var(--text-secondary);">
                        <div>${escapeHtml(quote?.source || item.source)}</div>
                        <div style="font-size: 0.7rem; color: var(--text-muted);">As-Of: ${escapeHtml(quote?.asOf || item.asOf)}</div>
                      </td>
                      <td style="text-align: right; white-space: nowrap;">
                        <div style="display: inline-flex; gap: 4px;">
                          ${!isRegistered
                            ? `<button
                                type="button"
                                class="btn-secondary"
                                style="font-size: 0.75rem; padding: 4px 8px;"
                                data-action="quick-register-instrument"
                                data-symbol="${escapeAttr(item.symbol)}"
                                data-name="${escapeAttr(item.name)}"
                                data-currency="${escapeAttr(itemCurrency)}"
                                title="Añadir a inventario (sin asiento ni cambio de saldo)"
                              >
                                ➕ Registrar
                              </button>`
                            : `<span class="badge" style="font-size: 0.75rem; background-color: var(--success-bg); color: var(--success-text);">Registrado</span>`
                          }
                          <button
                            type="button"
                            class="btn-primary"
                            style="font-size: 0.75rem; padding: 4px 10px;"
                            data-action="open-buy-modal"
                            data-instrument-id="${escapeAttr(item.symbol)}"
                            data-currency="${escapeAttr(itemCurrency)}"
                            data-price="${itemPrice !== undefined ? (Number(itemPrice) / 100).toFixed(2) : ""}"
                            title="Comprar unidades de este instrumento"
                          >
                            🛒 Comprar
                          </button>
                        </div>
                      </td>
                    </tr>
                  `;
                }).join("");
              })()}
            </tbody>
          </table>
        </div>
      </div>

      <!-- MODAL A: Registrar Instrumento (Sólo inventario, sin asiento contable ni cambio de saldo) -->
      <div id="register-instrument-modal" class="modal-overlay" style="display: none;" role="dialog" aria-modal="true" aria-labelledby="reg-inst-title">
        <div class="modal-content" style="max-width: 500px;">
          <div class="card-header">
            <h3 class="card-title" id="reg-inst-title">Registrar Instrumento en Inventario</h3>
            <button class="btn-secondary" style="padding: 2px 8px;" data-action="close-register-instrument-modal" aria-label="Cerrar">&times;</button>
          </div>
          <div style="background-color: var(--bg-tertiary); border-left: 3px solid var(--accent); padding: 10px 14px; border-radius: 4px; font-size: 0.85rem; margin-bottom: 14px;">
            <strong>Inventario informativo:</strong> Esta acción únicamente da de alta el instrumento en tu catálogo. <em>No genera ningún asiento contable ni altera tus saldos de efectivo o banco</em>.
          </div>
          <form id="register-instrument-form" data-form="register-instrument">
            <div style="margin-bottom: 12px;">
              <label for="reg-symbol" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Símbolo / Ticker / ISIN:</label>
              <input type="text" id="reg-symbol" data-testid="reg-symbol" required placeholder="Ej: AAPL, VOO, PERU30..." style="width: 100%; font-family: var(--font-mono); text-transform: uppercase;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="reg-name" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Nombre descriptivo:</label>
              <input type="text" id="reg-name" data-testid="reg-name" required placeholder="Ej: Apple Inc., Vanguard S&P 500..." style="width: 100%;" />
            </div>
            <div style="margin-bottom: 16px;">
              <label for="reg-currency" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Moneda del Instrumento:</label>
              <select id="reg-currency" data-testid="reg-currency" style="width: 100%;">
                <option value="PEN" ${currency === "PEN" ? "selected" : ""}>PEN (S/)</option>
                <option value="USD" ${currency === "USD" ? "selected" : ""}>USD ($)</option>
                <option value="EUR" ${currency === "EUR" ? "selected" : ""}>EUR (€)</option>
              </select>
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 8px;">
              <button type="button" class="btn-secondary" data-action="close-register-instrument-modal">Cancelar</button>
              <button type="submit" class="btn-primary" data-testid="confirm-register-instrument-btn">Registrar Instrumento</button>
            </div>
          </form>
        </div>
      </div>

      <!-- MODAL B: Comprar Instrumento (Validación de saldo + Asiento contable Debe Inversión / Haber Liquidez) -->
      <div id="buy-instrument-modal" class="modal-overlay" style="display: none;" role="dialog" aria-modal="true" aria-labelledby="buy-inst-title">
        <div class="modal-content" style="max-width: 520px;">
          <div class="card-header">
            <h3 class="card-title" id="buy-inst-title">🛒 Comprar Instrumento de Inversión</h3>
            <button class="btn-secondary" style="padding: 2px 8px;" data-action="close-buy-modal" aria-label="Cerrar">&times;</button>
          </div>
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 14px;">
            Genera un asiento de compra equilibrado: <strong>Debe Inversión</strong> y <strong>Haber Cuenta de Origen</strong>. Se valida saldo suficiente antes de ejecutar.
          </p>
          <form id="buy-instrument-form" data-form="buy-instrument">
            <div style="margin-bottom: 12px;">
              <label for="buy-instrument-select" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Instrumento:</label>
              <select id="buy-instrument-select" data-testid="buy-instrument-select" required style="width: 100%;">
                <optgroup label="Instrumentos Registrados">
                  ${activeInvestmentAccounts.map((a) => `<option value="${escapeAttr(a.instrumentId!)}" data-currency="${escapeAttr(a.currency)}">${escapeHtml(a.instrumentId!)} — ${escapeHtml(a.name)} (${escapeHtml(a.currency)})</option>`).join("")}
                </optgroup>
                <optgroup label="Catálogo Informativo">
                  ${INVESTMENT_CATALOG.map((c) => `<option value="${escapeAttr(c.symbol)}" data-currency="${escapeAttr(c.currency)}" data-price="${(Number(c.referencePriceMinor)/100).toFixed(2)}" data-name="${escapeAttr(c.name)}">${escapeHtml(c.symbol)} — ${escapeHtml(c.name)} (${escapeHtml(c.currency)})</option>`).join("")}
                </optgroup>
              </select>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px;">
              <div>
                <label for="buy-quantity" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cantidad de unidades:</label>
                <input type="number" step="1" min="1" id="buy-quantity" data-testid="buy-quantity" required placeholder="Ej: 5" style="width: 100%; font-weight: 600;" />
              </div>
              <div>
                <label for="buy-price" id="buy-price-label" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Precio Unitario (editable):</label>
                <input type="number" step="0.0001" min="0.0001" id="buy-price" data-testid="buy-price" required placeholder="0.00" style="width: 100%; font-weight: 600;" />
              </div>
            </div>

            <div style="margin-bottom: 12px;">
              <label for="buy-source-account" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cuenta de Origen de Fondos (Haber):</label>
              <select id="buy-source-account" data-testid="buy-source-account" required style="width: 100%;">
                ${liquidityAccounts.map((acc) => {
                  const bal = accountBalance(acc.accountId, rawEntries, acc.currency || currency, scale);
                  return `<option value="${escapeAttr(acc.accountId)}" data-balance-minor="${bal.amountMinor}" data-currency="${escapeAttr(acc.currency || currency)}">
                    ${escapeHtml(acc.name)} — Saldo disponible: ${formatMinorUnits(bal.amountMinor, acc.currency || currency, scale)}
                  </option>`;
                }).join("")}
              </select>
            </div>

            <!-- Previsualización de Importe y Validación de Saldo -->
            <div id="buy-summary-box" style="background-color: var(--bg-tertiary); border-radius: 6px; padding: 10px 14px; margin-bottom: 16px; font-size: 0.85rem;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span>Total Requerido:</span>
                <span id="buy-total-amount" style="font-weight: 700; font-family: var(--font-mono);">0.00</span>
              </div>
              <div id="buy-balance-feedback" style="font-size: 0.8rem; color: var(--text-secondary);">
                Selecciona los valores para verificar el saldo.
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px;">
              <button type="button" class="btn-secondary" data-action="close-buy-modal">Cancelar</button>
              <button type="submit" class="btn-primary" id="btn-confirm-buy" data-testid="confirm-buy-btn">Confirmar Compra</button>
            </div>
          </form>
        </div>
      </div>

      <!-- MODAL C: Vender Instrumento (No permite vender más de disponible + Asiento Debe Liquidez / Haber Inversión) -->
      <div id="sell-instrument-modal" class="modal-overlay" style="display: none;" role="dialog" aria-modal="true" aria-labelledby="sell-inst-title">
        <div class="modal-content" style="max-width: 520px;">
          <div class="card-header">
            <h3 class="card-title" id="sell-inst-title">💰 Vender Posición de Inversión</h3>
            <button class="btn-secondary" style="padding: 2px 8px;" data-action="close-sell-modal" aria-label="Cerrar">&times;</button>
          </div>
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 14px;">
            Genera un asiento de venta: <strong>Debe Cuenta Destino</strong> y <strong>Haber Inversión</strong>. No se permite vender más cantidad de la disponible.
          </p>
          <form id="sell-instrument-form" data-form="sell-instrument">
            <div style="margin-bottom: 12px;">
              <label for="sell-instrument-select" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Posición a Vender:</label>
              <select id="sell-instrument-select" data-testid="sell-instrument-select" required style="width: 100%;">
                ${positions.filter((p) => p.quantityMinor > 0n).map((p) => `
                  <option value="${escapeAttr(p.accountId)}" data-instrument-id="${escapeAttr(p.instrumentId)}" data-qty="${p.quantityMinor}" data-currency="${escapeAttr(p.currency)}">
                    ${escapeHtml(p.accountName)} (${escapeHtml(p.instrumentId)}) — Disponible: ${p.quantityMinor.toString()} uds.
                  </option>
                `).join("")}
              </select>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px;">
              <div>
                <label for="sell-quantity" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">
                  Cantidad a Vender:
                </label>
                <input type="number" step="1" min="1" id="sell-quantity" data-testid="sell-quantity" required placeholder="Uds a vender" style="width: 100%; font-weight: 600;" />
                <div id="sell-max-qty-hint" style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">
                  Disponible: 0
                </div>
              </div>
              <div>
                <label for="sell-price" id="sell-price-label" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Precio Unitario Venta:</label>
                <input type="number" step="0.0001" min="0.0001" id="sell-price" data-testid="sell-price" required placeholder="0.00" style="width: 100%; font-weight: 600;" />
              </div>
            </div>

            <div style="margin-bottom: 12px;">
              <label for="sell-dest-account" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cuenta Destino de Fondos (Debe):</label>
              <select id="sell-dest-account" data-testid="sell-dest-account" required style="width: 100%;">
                ${liquidityAccounts.map((acc) => `<option value="${escapeAttr(acc.accountId)}" data-currency="${escapeAttr(acc.currency || currency)}">
                  ${escapeHtml(acc.name)} (${escapeHtml(acc.currency || currency)})
                </option>`).join("")}
              </select>
            </div>

            <div id="sell-summary-box" style="background-color: var(--bg-tertiary); border-radius: 6px; padding: 10px 14px; margin-bottom: 16px; font-size: 0.85rem;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span>Total a Cobrar:</span>
                <span id="sell-total-amount" style="font-weight: 700; font-family: var(--font-mono);">0.00</span>
              </div>
              <div id="sell-qty-feedback" style="font-size: 0.8rem; color: var(--text-secondary);">
                Introduce la cantidad a vender.
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px;">
              <button type="button" class="btn-secondary" data-action="close-sell-modal">Cancelar</button>
              <button type="submit" class="btn-primary" id="btn-confirm-sell" data-testid="confirm-sell-btn">Confirmar Venta</button>
            </div>
          </form>
        </div>
      </div>

      <!-- MODAL D: Cotización Manual (FR-MKT-7) -->
      <div id="manual-quote-modal" class="modal-overlay" style="display: none;" role="dialog" aria-modal="true" aria-labelledby="mq-title">
        <div class="modal-content" style="max-width: 500px;">
          <div class="card-header">
            <h3 class="card-title" id="mq-title">Fijar Cotización Manual</h3>
            <button class="btn-secondary" style="padding: 2px 8px;" data-action="close-manual-quote-modal" aria-label="Cerrar">&times;</button>
          </div>
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 14px;">
            El precio manual prevalece sobre fuentes externas y se guarda localmente (FR-MKT-7).
          </p>
          <form id="manual-quote-form" data-form="manual-quote">
            <div style="margin-bottom: 12px;">
              <label for="mq-instrument" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">
                Instrumento (selecciona o escribe cualquier ticker libre, ej: MU, AMZN):
              </label>
              <input
                type="text"
                id="mq-instrument"
                data-testid="mq-instrument"
                list="mq-instruments-datalist"
                required
                placeholder="Ej: MU, AMZN, NVDA, AAPL..."
                style="width: 100%; font-family: var(--font-mono); text-transform: uppercase;"
                value="${positions[0]?.instrumentId || 'AAPL'}"
              />
              <datalist id="mq-instruments-datalist">
                ${positions.map((p) => `<option value="${escapeAttr(p.instrumentId)}">${escapeHtml(p.instrumentId)} (${escapeHtml(p.accountName)})</option>`).join("")}
                ${INVESTMENT_CATALOG.filter((c) => !positions.some((p) => p.instrumentId === c.symbol)).map((c) => `<option value="${escapeAttr(c.symbol)}">${escapeHtml(c.symbol)} — ${escapeHtml(c.name)}</option>`).join("")}
              </datalist>
            </div>
            <div style="margin-bottom: 12px;">
              <label for="mq-price" id="mq-price-label" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">
                Precio Unitario (${currency === "PEN" ? "S/" : currency === "USD" ? "$" : "€"}):
              </label>
              <input type="number" step="0.0001" min="0.0001" id="mq-price" data-testid="mq-price" required placeholder="Ej: 115.45" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 16px;">
              <label for="mq-date" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Fecha As-Of:</label>
              <input type="date" id="mq-date" data-testid="mq-date" required style="width: 100%;" value="${escapeAttr(new Date().toISOString().slice(0, 10))}" />
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 8px;">
              <button type="button" class="btn-secondary" data-action="close-manual-quote-modal">Cancelar</button>
              <button type="submit" class="btn-primary" data-testid="submit-mq-btn">Guardar Cotización</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  `;
}
